/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import { Backend } from "@/backend/base";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  LexicalTypeaheadMenuPlugin,
  MenuOption,
  MenuTextMatch,
  useBasicTypeaheadTriggerMatch,
} from "@lexical/react/LexicalTypeaheadMenuPlugin";
import { formatDistanceToNow } from "date-fns";
import {
  $isElementNode,
  ElementNode,
  RootNode,
  SerializedEditorState,
  SerializedLexicalNode,
  SerializedParagraphNode,
  SerializedRootNode,
  TextNode,
} from "lexical";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as ReactDOM from "react-dom";

/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import { DropdownClassName } from "@/components/constants/class-names";
import { commandScore } from "@/components/text-editor/command-score";
import { editorContextStore } from "@/contexts/editor-context";
import {
  customModelCaptionTrigger,
  customModelMentionTrigger,
  CustomModelPlaygroundPromptEditorState,
  CustomModelScaleConfig,
  CustomModelScaleConfigs,
  CustomModelTrainingItem,
  CustomModelTrainingStatus,
  getModelIdFromTraining,
  getModelTrainingMentionName,
  getModelTriggerWord,
  getTrainingDisplayName,
} from "@/core/common/types";
import { Assets } from "@/core/controllers/assets";
import { classNames } from "@/core/utils/classname-utils";
import { getCustomModelDisplayCategory } from "@/core/utils/custom-model-utils";
import { dedupeItemsByKey } from "@/core/utils/object-utils";
import { debugError, debugLog } from "@/core/utils/print-utilts";
import { getTextContentFromPromptState } from "@/core/utils/text-utils";
import { Timestamp } from "firebase/firestore";
import {
  $applyNodeReplacement,
  type DOMConversionMap,
  type DOMConversionOutput,
  type DOMExportOutput,
  type EditorConfig,
  type LexicalNode,
  type NodeKey,
  type SerializedTextNode,
  type Spread,
} from "lexical";
import { useInView } from "react-intersection-observer";
import { ImageComponent } from "../utils/image";

export type MentionNodeExtraProps = {
  modelId: string;
  modelDisplayName: string;
  trainingId: string;
  trainingDisplayName: string;
  caption?: string;
  scale: number;
};

export type SerializedMentionNode = Spread<MentionNodeExtraProps, SerializedTextNode>;

export function isSerializedMentionNode(node: any): node is SerializedMentionNode {
  return node?.type === "mention";
}

export enum MentionCaptionNodeType {
  Prefix = "prefix",
  Suffix = "suffix",
}

export type MentionCaptionNodeExtraProps = MentionNodeExtraProps & {
  captionType: MentionCaptionNodeType;
};

export type SerializedMentionCaptionNode = Spread<MentionCaptionNodeExtraProps, SerializedTextNode>;

export function isSerializedMentionCaptionNode(node: any): node is SerializedMentionCaptionNode {
  return node?.type === "mention-caption";
}

function $convertMentionElement(domNode: HTMLElement): DOMConversionOutput | null {
  const textContent = domNode.textContent;

  if (textContent !== null) {
    const node = $createMentionNode({
      text: textContent,
      trainingId: textContent,
      trainingDisplayName: textContent,
      modelDisplayName: textContent,
      modelId: textContent,
      caption: textContent,
      scale: 1,
    });
    return {
      node,
    };
  }

  return null;
}

const mentionCaptionStyle = `
background-color: rgba(77, 124, 15, 0.1);
padding-top:1px;
padding-bottom: 1px;
border-radius: 0.125rem;`;

export class MentionCaptionNode extends TextNode {
  __trainingId: string;
  __trainingDisplayName: string;
  __modelId: string;
  __modelDisplayName: string;
  __caption?: string;
  __scale: number = 1;
  __captionType: MentionCaptionNodeType; // <-- NEW FIELD

  static getType(): string {
    return "mention-caption";
  }

  /**
   * Creates a copy of the MentionCaptionNode
   */
  static clone(node: MentionCaptionNode): MentionCaptionNode {
    return new MentionCaptionNode({
      modelId: node.__modelId,
      modelDisplayName: node.__modelDisplayName,
      trainingId: node.__trainingId,
      trainingDisplayName: node.__trainingDisplayName,
      text: node.__text,
      key: node.__key,
      caption: node.__caption,
      scale: node.__scale,
      captionType: node.__captionType, // carry over the captionType
    });
  }

  /**
   * Import from serialized JSON.
   */
  static importJSON(serializedNode: SerializedMentionCaptionNode): MentionCaptionNode {
    const node = $createMentionCaptionNode({
      modelId: serializedNode.modelId,
      modelDisplayName: serializedNode.modelDisplayName,
      trainingId: serializedNode.trainingId,
      trainingDisplayName: serializedNode.trainingDisplayName,
      text: serializedNode.text,
      caption: serializedNode.caption,
      scale: serializedNode.scale,
      captionType: serializedNode.captionType, // use the imported captionType
    });
    node.setTextContent(serializedNode.text);
    node.setFormat(serializedNode.format);
    node.setDetail(serializedNode.detail);
    node.setMode(serializedNode.mode);
    node.setStyle(serializedNode.style);
    return node;
  }

  /**
   * Node constructor.
   */
  constructor({
    modelId,
    modelDisplayName,
    trainingId,
    trainingDisplayName,
    text,
    key,
    caption,
    scale,
    captionType,
  }: MentionCaptionNodeExtraProps & {
    text?: string;
    key?: NodeKey;
  }) {
    super(text ?? trainingId, key);
    this.__trainingId = trainingId;
    this.__modelId = modelId;
    this.__modelDisplayName = modelDisplayName;
    this.__trainingDisplayName = trainingDisplayName;
    this.__caption = caption;
    this.__scale = scale ?? 1;
    this.__captionType = captionType; // <-- store it here
  }

  /**
   * Export to JSON for serialization.
   */
  exportJSON(): SerializedMentionCaptionNode {
    return {
      ...super.exportJSON(),
      trainingId: this.__trainingId,
      trainingDisplayName: this.__trainingDisplayName,
      modelId: this.__modelId,
      modelDisplayName: this.__modelDisplayName,
      caption: this.__caption,
      scale: this.__scale,
      type: "mention-caption",
      version: 1,
      captionType: this.__captionType, // <-- include captionType
    };
  }

  /**
   * DOM creation: apply style + class.
   */
  createDOM(config: EditorConfig): HTMLElement {
    const dom = super.createDOM(config);
    dom.style.cssText = mentionCaptionStyle;
    dom.className = "mention-caption";
    return dom;
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement("span");
    element.setAttribute("data-lexical-mention-caption", "true");
    element.textContent = this.__text;
    return { element };
  }

  static importDOM(): DOMConversionMap | null {
    return {
      span: (domNode: HTMLElement) => {
        if (!domNode.hasAttribute("data-lexical-mention-caption")) {
          return null;
        }
        return {
          conversion: $convertMentionCaptionElement,
          priority: 1,
        };
      },
    };
  }

  /**
   * A mention caption is an entity, so we disallow insertion
   * of text before or after it.
   */
  isTextEntity(): true {
    return true;
  }

  canInsertTextBefore(): boolean {
    return false;
  }

  canInsertTextAfter(): boolean {
    return false;
  }

  getModelId() {
    return this.__modelId;
  }

  getCaptionType() {
    return this.__captionType;
  }
}

/**
 * Converts a DOM element with `data-lexical-mention-caption` into a MentionCaptionNode.
 */
function $convertMentionCaptionElement(domNode: HTMLElement): DOMConversionOutput | null {
  const textContent = domNode.textContent;

  if (textContent !== null) {
    const node = $createMentionCaptionNode({
      text: textContent,
      trainingId: textContent,
      trainingDisplayName: textContent,
      modelDisplayName: textContent,
      modelId: textContent,
      caption: textContent,
      scale: 1,
      captionType: MentionCaptionNodeType.Suffix,
    });
    return {
      node,
    };
  }

  return null;
}

/**
 * Creates a new MentionCaptionNode instance and applies node replacement.
 */
export function $createMentionCaptionNode({
  text,
  trainingId,
  trainingDisplayName,
  modelId,
  modelDisplayName,
  caption,
  scale,
  captionType,
}: CreateMentionNodeArgs & {
  captionType: MentionCaptionNodeType;
}): MentionCaptionNode {
  const mentionCaptionNode = new MentionCaptionNode({
    trainingId,
    trainingDisplayName,
    text,
    modelId,
    modelDisplayName,
    caption,
    scale,
    captionType,
  });
  mentionCaptionNode.setMode("segmented").toggleDirectionless();
  return $applyNodeReplacement(mentionCaptionNode);
}

/**
 * Type-guard for MentionCaptionNode instances.
 */
export function $isMentionCaptionNode(
  node: LexicalNode | null | undefined,
): node is MentionCaptionNode {
  return node instanceof MentionCaptionNode;
}

const mentionStyle = `
color: rgb(190 242 100);
background-color: rgba(190, 242, 100, 0.1);
padding-left: 3px;
padding-right: 3px;
padding-top:1px;
padding-bottom: 1px;
border-radius: 0.125rem;`;
export class MentionNode extends TextNode {
  __trainingId: string;

  __trainingDisplayName: string;

  __modelId: string;

  __modelDisplayName: string;

  __caption?: string;

  __scale: number = 1;

  static getType(): string {
    return "mention";
  }

  static clone(node: MentionNode): MentionNode {
    return new MentionNode({
      modelId: node.__modelId,
      modelDisplayName: node.__modelDisplayName,
      trainingId: node.__trainingId,
      trainingDisplayName: node.__trainingDisplayName,
      text: node.__text,
      key: node.__key,
      caption: node.__caption,
      scale: node.__scale,
    });
  }
  static importJSON(serializedNode: SerializedMentionNode): MentionNode {
    const node = $createMentionNode({
      modelId: serializedNode.modelId,
      modelDisplayName: serializedNode.modelDisplayName,
      trainingId: serializedNode.trainingId,
      trainingDisplayName: serializedNode.trainingDisplayName,
      text: serializedNode.text,
      caption: serializedNode.caption,
      scale: serializedNode.scale,
    });
    node.setTextContent(serializedNode.text);
    node.setFormat(serializedNode.format);
    node.setDetail(serializedNode.detail);
    node.setMode(serializedNode.mode);
    node.setStyle(serializedNode.style);
    return node;
  }

  constructor({
    modelId,
    modelDisplayName,
    trainingId,
    trainingDisplayName,
    text,
    key,
    caption,
  }: MentionNodeExtraProps & {
    text?: string;
    key?: NodeKey;
  }) {
    super(text ?? trainingId, key);
    this.__trainingId = trainingId;
    this.__modelId = modelId;
    this.__modelDisplayName = modelDisplayName;
    this.__trainingDisplayName = trainingDisplayName;
    this.__caption = caption;
  }

  exportJSON(): SerializedMentionNode {
    return {
      ...super.exportJSON(),
      trainingId: this.__trainingId,
      trainingDisplayName: this.__trainingDisplayName,
      modelId: this.__modelId,
      modelDisplayName: this.__modelDisplayName,
      caption: this.__caption,
      scale: this.__scale,
      type: "mention",
      version: 1,
    };
  }

  createDOM(config: EditorConfig): HTMLElement {
    const dom = super.createDOM(config);
    dom.style.cssText = mentionStyle;
    dom.className = "mention";
    return dom;
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement("span");
    element.setAttribute("data-lexical-mention", "true");
    element.textContent = this.__text;
    return { element };
  }

  static importDOM(): DOMConversionMap | null {
    return {
      span: (domNode: HTMLElement) => {
        if (!domNode.hasAttribute("data-lexical-mention")) {
          return null;
        }
        return {
          conversion: $convertMentionElement,
          priority: 1,
        };
      },
    };
  }

  isTextEntity(): true {
    return true;
  }

  canInsertTextBefore(): boolean {
    return false;
  }

  canInsertTextAfter(): boolean {
    return false;
  }

  getMention() {
    return this.__trainingId;
  }

  getModelId() {
    return this.__modelId;
  }

  setScale(newScale: number) {
    const writable = this.getWritable();
    writable.__scale = newScale;
  }
}

export type CreateMentionNodeArgs = MentionNodeExtraProps & {
  text?: string;
};

export function $createMentionNode({
  text,
  trainingId: mentionName,
  trainingDisplayName,
  modelId,
  modelDisplayName,
  caption,
  scale,
}: CreateMentionNodeArgs): MentionNode {
  const mentionNode = new MentionNode({
    trainingId: mentionName,
    trainingDisplayName,
    text,
    modelId,
    modelDisplayName,
    caption,
    scale,
  });
  mentionNode.setMode("segmented").toggleDirectionless();
  return $applyNodeReplacement(mentionNode);
}

export function $isMentionNode(node: LexicalNode | null | undefined): node is MentionNode {
  return node instanceof MentionNode;
}

export function getAllMentionNodes(rootNode: RootNode | ElementNode): Array<MentionNode> {
  const mentionNodes: MentionNode[] = [];
  let child: LexicalNode | null = rootNode.getFirstChild();

  while (child !== null) {
    if ($isMentionNode(child)) {
      mentionNodes.push(child);
    }
    if ($isElementNode(child)) {
      const subChildrenNodes = getAllMentionNodes(child);
      mentionNodes.push(...subChildrenNodes);
    }
    child = child.getNextSibling();
  }

  return mentionNodes;
}

const PUNCTUATION = "\\.,\\+\\*\\?\\$\\@\\|#{}\\(\\)\\^\\-\\[\\]\\\\/!%'\"~=<>_:;";
const NAME = "\\b[A-Z][^\\s" + PUNCTUATION + "]";

const DocumentMentionsRegex = {
  NAME,
  PUNCTUATION,
};

const PUNC = DocumentMentionsRegex.PUNCTUATION;

const TRIGGERS = [customModelMentionTrigger].join("");

// Chars we expect to see in a mention (non-space, non-punctuation).
const VALID_CHARS = "[^" + TRIGGERS + PUNC + "\\s]";

// Non-standard series of chars. Each series must be preceded and followed by
// a valid char.
const VALID_JOINS =
  "(?:" +
  "\\.[ |$]|" + // E.g. "r. " in "Mr. Smith"
  " |" + // E.g. " " in "Josh Duck"
  "[" +
  PUNC +
  "]|" + // E.g. "-' in "Salier-Hellendag"
  ")";

const LENGTH_LIMIT = 75;

const AtSignMentionsRegex = new RegExp(
  "(^|\\s|\\()(" +
    "[" +
    TRIGGERS +
    "]" +
    "((?:" +
    VALID_CHARS +
    VALID_JOINS +
    "){0," +
    LENGTH_LIMIT +
    "})" +
    ")$",
);

// 50 is the longest alias length limit.
const ALIAS_LENGTH_LIMIT = 50;

// Regex used to match alias.
const AtSignMentionsRegexAliasRegex = new RegExp(
  "(^|\\s|\\()(" +
    "[" +
    TRIGGERS +
    "]" +
    "((?:" +
    VALID_CHARS +
    "){0," +
    ALIAS_LENGTH_LIMIT +
    "})" +
    ")$",
);

// At most, 5 suggestions are shown in the popup.
const SUGGESTION_LIST_LENGTH_LIMIT = 100;

const mentionsCache = new Map();

interface MentionOption extends MentionNodeExtraProps {
  name: string;
  thumbnailStoragePath?: string;
}

type CustomModelTrainingItemWithModelDisplayName = CustomModelTrainingItem & {
  modelId: string;
  modelDisplayName: string;
};

async function getModelDisplayName({ backend, modelId }: { backend: Backend; modelId: string }) {
  const customModelInfo = await backend.getCustomModelInfo(modelId);

  return customModelInfo?.displayName ?? "Uknown";
}

async function addModelDisplayNameToTrainingItem({
  backend,
  training,
}: {
  backend: Backend;
  training: CustomModelTrainingItem;
}): Promise<CustomModelTrainingItemWithModelDisplayName | undefined> {
  const modelId = getModelIdFromTraining(training);

  if (!modelId) {
    return undefined;
  }

  const modelDisplayName = await getModelDisplayName({
    backend,
    modelId,
  });

  return {
    ...training,
    modelId,
    modelDisplayName,
  };
}

type GetTrainingThumbnailStoragePathCallback = (
  training: CustomModelTrainingItemWithModelDisplayName,
) => string | undefined;

function getMentionOptionFromTraining({
  training,
  getTrainingThumbnailStoragePath,
}: {
  training: CustomModelTrainingItemWithModelDisplayName;
  getTrainingThumbnailStoragePath: GetTrainingThumbnailStoragePathCallback;
}): MentionOption {
  return {
    name: getModelTrainingMentionName({
      training,
      modelDisplayName: training.modelDisplayName,
    }),
    trainingId: training.id,
    trainingDisplayName: training.displayName ?? getTrainingDisplayName(training),
    modelDisplayName: training.modelDisplayName,
    modelId: training.modelId,
    caption: training.caption,
    thumbnailStoragePath: getTrainingThumbnailStoragePath(training),
    scale: 1,
  };
}

function getMentionOptionsFromTrainings({
  trainings,
  getTrainingThumbnailStoragePath,
}: {
  trainings: CustomModelTrainingItemWithModelDisplayName[];
  getTrainingThumbnailStoragePath: GetTrainingThumbnailStoragePathCallback;
}) {
  return trainings
    .filter((training) => training.status === CustomModelTrainingStatus.Succeeded)
    .map((training) =>
      getMentionOptionFromTraining({
        training,
        getTrainingThumbnailStoragePath,
      }),
    );
}

class CustomModelLookupService {
  private customModelTrainingsCache: Record<string, CustomModelTrainingItem[]> = {};

  private async refreshCustomModelTrainings({ modelIds }: { modelIds: string[] }) {
    const { backend } = editorContextStore.getState();

    if (!backend) {
      return;
    }

    this.customModelTrainingsCache = Object.fromEntries(
      await Promise.all(
        modelIds.map(async (modelId) => {
          const trainings = await backend.getCustomModelTrainings({
            modelId,
          });

          return [
            modelId,
            trainings.map((training) => ({
              ...training,
              modelId,
            })),
          ];
        }),
      ),
    );
  }

  private getTrainingsFromCache({ modelIds }: { modelIds: string[] }) {
    const modelIdsSet = new Set(modelIds);

    return Object.entries(this.customModelTrainingsCache)
      .filter(([modelId]) => modelIdsSet.has(modelId))
      .reduce<CustomModelTrainingItem[]>((prevTrainings, [, trainings]) => {
        prevTrainings.push(...trainings);
        return prevTrainings;
      }, []);
  }

  private async getCustomModelTrainings({ modelIds }: { modelIds: string[] }) {
    try {
      // Check if we need to refresh the cache

      if (modelIds.every((modelId) => this.customModelTrainingsCache[modelId] != null)) {
        return this.getTrainingsFromCache({
          modelIds,
        });
      }

      await this.refreshCustomModelTrainings({
        modelIds,
      });

      return this.getTrainingsFromCache({
        modelIds,
      });
    } catch (error) {
      debugError(`Cannot get trainings for models ${modelIds}: `, error);
    }
  }

  static sortMentionNodeOptionsByModelId({
    options,
    targetModelId,
  }: {
    options: MentionOption[];
    targetModelId?: string;
  }): MentionOption[] {
    if (!targetModelId) {
      return options;
    }
    return options.sort((a, b) => {
      if (a.modelId === targetModelId && b.modelId !== targetModelId) {
        return -1; // Move `a` to the top
      } else if (a.modelId !== targetModelId && b.modelId === targetModelId) {
        return 1; // Move `b` to the top
      } else {
        return 0; // Keep the order the same
      }
    });
  }

  search(searchString: string, callback: (results: Array<MentionOption>) => void): void {
    const { backend, customModels, publicCustomModels, customModelId } =
      editorContextStore.getState();

    if (!backend) {
      debugError("Backend is not initialized yet.");
      return;
    }

    // debugLog('Start getting custom model trainings with public models:\n', Object.keys(publicCustomModels));

    const getTrainingThumbnailStoragePath: GetTrainingThumbnailStoragePathCallback = (training) => {
      try {
        const modelId = training.modelId;

        const customModel = customModels[modelId];

        return customModel.thumbnailStoragePath;
      } catch (error) {
        debugError("Error getting training thumbnail storage path: ", error);
        return undefined;
      }
    };

    this.getCustomModelTrainings({
      modelIds: Object.keys({
        ...customModels,
        ...publicCustomModels,
      }),
    })
      .then((trainings) => {
        if (!trainings) {
          return [];
        }

        return Promise.all(
          trainings.map((training) =>
            addModelDisplayNameToTrainingItem({
              backend,
              training,
            }),
          ),
        );
      })
      .then(
        (trainings) => trainings.filter(Boolean) as CustomModelTrainingItemWithModelDisplayName[],
      )
      .then((trainings) =>
        dedupeItemsByKey<CustomModelTrainingItemWithModelDisplayName, "id">(trainings, "id"),
      )
      .then((trainings) =>
        getMentionOptionsFromTrainings({
          trainings,
          getTrainingThumbnailStoragePath,
        }),
      )
      .then((mentionOptions) => {
        if (!searchString.trim()) {
          return CustomModelLookupService.sortMentionNodeOptionsByModelId({
            options: mentionOptions,
            targetModelId: customModelId,
          });
        }

        return mentionOptions
          .map((option) => {
            const score = commandScore(option.name, searchString);

            return {
              ...option,
              score,
            };
          })
          .filter(({ score }) => score > 0.5)
          .sort(({ score: scoreA }, { score: scoreB }) => scoreA - scoreB);
      })
      .then(callback);
  }
}

const customModelLookupService = new CustomModelLookupService();

function useMentionLookupService(mentionString: string | null) {
  const [results, setResults] = useState<Array<MentionOption>>([]);

  useEffect(() => {
    const cachedResults = mentionsCache.get(mentionString);

    if (mentionString == null) {
      setResults([]);
      return;
    }

    if (cachedResults === null) {
      return;
    } else if (cachedResults !== undefined) {
      setResults(cachedResults);
      return;
    }

    mentionsCache.set(mentionString, null);
    customModelLookupService.search(mentionString, (newResults) => {
      debugLog("New results: ", newResults);
      mentionsCache.set(mentionString, newResults);
      setResults(newResults);
    });
  }, [mentionString]);

  return results;
}

function checkForAtSignMentions(text: string, minMatchLength: number): MenuTextMatch | null {
  let match = AtSignMentionsRegex.exec(text);

  if (match === null) {
    match = AtSignMentionsRegexAliasRegex.exec(text);
  }

  if (match !== null) {
    // The strategy ignores leading whitespace but we need to know it's
    // length to add it to the leadOffset
    const maybeLeadingWhitespace = match[1];

    const matchingString = match[3];

    if (matchingString.length >= minMatchLength) {
      return {
        leadOffset: Math.max(match.index + maybeLeadingWhitespace.length, 1),
        matchingString,
        replaceableString: match[2],
      };
    }
  }
  return null;
}

function getPossibleQueryMatch(text: string): MenuTextMatch | null {
  return checkForAtSignMentions(text, 0);
}

// #region Text to State

interface TriggerWordMatch {
  start: number;
  end: number;
  triggerWord: string;
}

export function getSerializedPromptEditorStateFromText({
  text,
  triggerWordToMentionNode,
}: {
  text: string;
  triggerWordToMentionNode: Record<string, SerializedMentionNode>;
}): SerializedEditorState {
  // Helper function to create a text node
  function createTextNode(textContent: string): SerializedTextNode {
    return {
      detail: 0,
      format: 0,
      mode: "normal",
      style: "",
      text: textContent,
      type: "text",
      version: 1,
    };
  }

  // Helper function to escape regex special characters
  function escapeRegExp(s: string): string {
    return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  }

  // Helper function to build regex from trigger words
  function buildTriggerWordsRegex(triggerWords: string[]): RegExp {
    const escapedWords = triggerWords.map(escapeRegExp);
    const pattern = escapedWords.sort((a, b) => b.length - a.length).join("|");
    return new RegExp(pattern, "g");
  }

  // Helper function to find all matches of trigger words in text
  function findAllTriggerWordMatches(text: string, triggerWords: string[]): TriggerWordMatch[] {
    const matches: TriggerWordMatch[] = [];
    if (triggerWords.length === 0) {
      return matches;
    }

    const regex = buildTriggerWordsRegex(triggerWords);
    let match;
    while ((match = regex.exec(text)) !== null) {
      matches.push({
        start: match.index,
        end: match.index + match[0].length,
        triggerWord: match[0],
      });
    }
    return matches;
  }

  // Helper function to get non-overlapping matches
  function getNonOverlappingMatches(
    matches: { start: number; end: number; triggerWord: string }[],
  ): { start: number; end: number; triggerWord: string }[] {
    // Sort matches by start index and prefer longer matches
    matches.sort((a, b) => {
      if (a.start !== b.start) {
        return a.start - b.start;
      } else {
        return b.end - b.start - (a.end - a.start);
      }
    });

    const nonOverlappingMatches: { start: number; end: number; triggerWord: string }[] = [];
    let lastEndIndex = -1;
    for (const match of matches) {
      if (match.start >= lastEndIndex) {
        nonOverlappingMatches.push(match);
        lastEndIndex = match.end;
      }
    }
    return nonOverlappingMatches;
  }

  // Get the list of trigger words
  const triggerWords = Object.keys(triggerWordToMentionNode);

  // Find all matches
  const matches = findAllTriggerWordMatches(text, triggerWords);

  // Get non-overlapping matches
  const nonOverlappingMatches = getNonOverlappingMatches(matches);

  // Build the nodes
  const nodes: SerializedLexicalNode[] = [];
  let currentIndex = 0;

  for (const match of nonOverlappingMatches) {
    if (match.start > currentIndex) {
      // Add text node for text before the match
      const textSegment = text.substring(currentIndex, match.start);
      nodes.push(createTextNode(textSegment));
    }

    // Add mention node for the matched trigger word
    const mentionNode = triggerWordToMentionNode[match.triggerWord];
    nodes.push(mentionNode);

    currentIndex = match.end;
  }

  // Add remaining text after the last match
  if (currentIndex < text.length) {
    const textSegment = text.substring(currentIndex);
    nodes.push(createTextNode(textSegment));
  }

  // Handle case with no trigger words
  if (nodes.length === 0) {
    nodes.push(createTextNode(text));
  }

  // Create a paragraph node to hold the nodes
  const paragraphNode: SerializedParagraphNode = {
    children: nodes,
    direction: "ltr",
    format: "",
    indent: 0,
    type: "paragraph",
    version: 1,
    textFormat: 0,
    textStyle: "",
  };

  // Create the root node
  const rootNode: SerializedRootNode = {
    children: [paragraphNode],
    direction: "ltr",
    format: "",
    indent: 0,
    type: "root",
    version: 1,
  };

  // Return the serialized editor state
  return {
    root: rootNode,
  };
}

// #endregion

class MentionTypeaheadOption extends MenuOption {
  name: string;
  modelId: string;
  modelDisplayName: string;
  trainingId: string;
  trainingDisplayName: string;
  caption?: string;
  scale: number;
  picture: JSX.Element;
  displayCategory: string;
  lastUpdated: Timestamp;

  constructor({
    name,
    trainingId,
    trainingDisplayName,
    modelId,
    modelDisplayName,
    picture,
    caption,
    scale = 1,
    displayCategory,
    lastUpdated,
  }: MentionOption & {
    picture: JSX.Element;
    displayCategory: string;
    lastUpdated: Timestamp;
  }) {
    super(name);
    this.name = name;
    this.modelId = modelId;
    this.modelDisplayName = modelDisplayName;
    this.trainingId = trainingId;
    this.trainingDisplayName = trainingDisplayName;
    this.picture = picture;
    this.displayCategory = displayCategory;
    this.lastUpdated = lastUpdated;
    this.caption = caption;
    this.scale = scale;
  }

  getMentionNodeArgs(): CreateMentionNodeArgs {
    return {
      text: this.name,
      modelDisplayName: this.modelDisplayName,
      trainingId: this.trainingId,
      trainingDisplayName: this.trainingDisplayName,
      modelId: this.modelId,
      caption: this.caption,
      scale: this.scale,
    };
  }
}

function MentionsTypeaheadMenuItem({
  index,
  isSelected,
  onClick,
  onMouseEnter,
  option,
}: {
  index: number;
  isSelected: boolean;
  onClick: () => void;
  onMouseEnter: () => void;
  option: MentionTypeaheadOption;
}) {
  let className = "item";
  if (isSelected) {
    className += " selected";
  }
  return (
    <li
      key={option.key}
      tabIndex={-1}
      className={classNames(
        className,
        "flex flex-row gap-2 items-center px-2 py-1 rounded-sm",
        isSelected ? "bg-zinc-800/50 outline outline-1 outline-zinc-800 text-lime-500" : "",
      )}
      ref={option.setRefElement}
      role="option"
      aria-selected={isSelected}
      id={"typeahead-item-" + index}
      onMouseEnter={onMouseEnter}
      onClick={onClick}
    >
      <div className="w-8 h-8 rounded overflow-hidden flex-shrink-0 bg-zinc-800">
        {option.picture}
      </div>
      <div className="flex flex-col items-start">
        <div className="flex flex-row items-center gap-2">
          <div className="text-zinc-300 max-w-[20vw] truncate group-hover:text-zinc-100">
            {option.name}
          </div>
          <div className="text-zinc-500 group-hover:text-zinc-100">{option.displayCategory}</div>
        </div>
        <div className="text-xs text-zinc-500 group-hover:text-zinc-100">
          Updated {formatDistanceToNow(option.lastUpdated.toDate())} ago
        </div>
      </div>
    </li>
  );
}

function CustomModelTrainingThumbnail({ thumbnailStoragePath }: { thumbnailStoragePath?: string }) {
  const [imageUrl, setImageUrl] = useState("");
  const storageManager = editorContextStore((state) => state.storageManager);

  const [elementRef, inView] = useInView();

  const displayImageUrl = useMemo(() => (inView ? imageUrl : ""), [inView, imageUrl]);

  useEffect(() => {
    if (!storageManager || !thumbnailStoragePath) {
      return;
    }

    Assets.loadAssetFromPath({
      path: thumbnailStoragePath,
      storageManager,
    })
      .then((imageUrl) => {
        if (!imageUrl) {
          return;
        }
        setImageUrl(imageUrl);
      })
      .catch(debugError);
  }, [storageManager, thumbnailStoragePath]);

  return (
    <div ref={elementRef} className="w-8 h-8 rounded-sm bg-zinc-800 overflow-hidden">
      {displayImageUrl && (
        <ImageComponent src={displayImageUrl} className="w-full h-full object-cover" />
      )}
    </div>
  );
}

export default function CustomModelsMentionsPlugin(): JSX.Element | null {
  const [editor] = useLexicalComposerContext();
  const [queryString, setQueryString] = useState<string | null>(null);
  const customModels = editorContextStore((state) => state.customModels);
  const results = useMentionLookupService(queryString);

  // Store initial position to prevent shifts during interactions
  const stablePositionRef = useRef<{ left: number; top: number } | null>(null);

  // Track if the dropdown has been positioned
  const hasPositionedRef = useRef(false);

  const checkForSlashTriggerMatch = useBasicTypeaheadTriggerMatch("/", {
    minLength: 0,
  });

  const options = useMemo(
    () =>
      results
        .map((result) => {
          const customModel = customModels?.[result.modelId];
          if (!customModel) {
            return null;
          }
          return new MentionTypeaheadOption({
            ...result,
            picture: (
              <CustomModelTrainingThumbnail thumbnailStoragePath={result.thumbnailStoragePath} />
            ),
            displayCategory: getCustomModelDisplayCategory({
              customModel,
            }),
            lastUpdated: customModel.timeModified,
          });
        })
        .filter(Boolean)
        .slice(0, SUGGESTION_LIST_LENGTH_LIMIT),
    [results, customModels],
  );

  const onSelectOption = useCallback(
    (
      selectedOption: MentionTypeaheadOption,
      nodeToReplace: TextNode | null,
      closeMenu: () => void,
    ) => {
      editor.update(() => {
        // Helper to check if text already exists at start/end of a node
        const textExistsAt = (
          node: LexicalNode | null,
          text: string,
          position: "start" | "end",
        ): boolean => {
          if (!(node instanceof TextNode)) {
            return false;
          }
          text = text.toLowerCase();
          const nodeText = node.getTextContent().toLowerCase();
          return position === "start" ? nodeText.startsWith(text) : nodeText.endsWith(text);
        };

        // Helper to insert caption text if it doesn't already exist
        const insertCaption = (
          text: string,
          position: "before" | "after",
          captionType: MentionCaptionNodeType,
        ) => {
          const sibling =
            position === "before" ? mentionNode.getPreviousSibling() : mentionNode.getNextSibling();
          const textPosition = position === "before" ? "end" : "start";

          // Skip if text already exists in the right place
          if (textExistsAt(sibling, text, textPosition)) {
            return;
          }

          // Create and insert the caption node
          const captionNode = $createMentionCaptionNode({
            text,
            trainingId: selectedOption.trainingId,
            trainingDisplayName: selectedOption.trainingDisplayName,
            modelId: selectedOption.modelId,
            modelDisplayName: selectedOption.modelDisplayName,
            caption: selectedOption.caption,
            scale: selectedOption.scale,
            captionType,
          });

          if (position === "before") {
            mentionNode.insertBefore(captionNode);
          } else {
            mentionNode.insertAfter(captionNode);
          }
        };

        // Helper to clean up existing caption nodes
        const removeExistingCaptionNodes = () => {
          const leftSibling = mentionNode.getPreviousSibling();
          if (
            $isMentionCaptionNode(leftSibling) &&
            leftSibling.getCaptionType() === MentionCaptionNodeType.Prefix
          ) {
            leftSibling.remove();
          }

          const rightSibling = mentionNode.getNextSibling();
          if (
            $isMentionCaptionNode(rightSibling) &&
            rightSibling.getCaptionType() === MentionCaptionNodeType.Suffix
          ) {
            rightSibling.remove();
          }
        };

        // Create the mention node and place it in the editor
        const mentionNode = $createMentionNode(selectedOption.getMentionNodeArgs());
        if (nodeToReplace) {
          nodeToReplace.replace(mentionNode);
        }
        mentionNode.select();

        // Remove any existing caption nodes
        removeExistingCaptionNodes();

        // Handle different model types
        const isStyleModel = selectedOption.displayCategory === "Style";

        if (isStyleModel) {
          // Style models always get "in style of " prefix
          insertCaption("in style of ", "before", MentionCaptionNodeType.Prefix);
        } else if (selectedOption.caption?.includes(customModelCaptionTrigger)) {
          // Regular models with captions
          const [prefixText, suffixText] = selectedOption.caption.split(customModelCaptionTrigger);

          if (prefixText) {
            insertCaption(prefixText, "before", MentionCaptionNodeType.Prefix);
          }

          if (suffixText) {
            insertCaption(suffixText, "after", MentionCaptionNodeType.Suffix);
          }
        }

        // Clean up and close menu
        closeMenu();
        hasPositionedRef.current = false;
        stablePositionRef.current = null;
      });
    },
    [editor],
  );

  const checkForMentionMatch = useCallback(
    (text: string) => {
      const slashMatch = checkForSlashTriggerMatch(text, editor);
      if (slashMatch !== null) {
        return null;
      }
      return getPossibleQueryMatch(text);
    },
    [checkForSlashTriggerMatch, editor],
  );

  // Reset position tracking when query changes or menu closes
  useEffect(() => {
    hasPositionedRef.current = false;
    stablePositionRef.current = null;
  }, [queryString, results.length]);

  return (
    <LexicalTypeaheadMenuPlugin<MentionTypeaheadOption>
      onQueryChange={setQueryString}
      onSelectOption={onSelectOption as any}
      triggerFn={checkForMentionMatch}
      options={options}
      anchorClassName="z-100"
      menuRenderFn={(
        anchorElementRef,
        { selectedIndex, selectOptionAndCleanUp, setHighlightedIndex },
      ) => {
        if (!anchorElementRef.current || !results.length) {
          return null;
        }

        // Calculate position on each render but only apply changes to the DOM once
        // This ensures we always have the right coordinates while preventing shifting
        const rect = anchorElementRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const viewportWidth = window.innerWidth;

        // Dropdown dimensions (estimated)
        const dropdownWidth = 400;
        const dropdownHeight = Math.min(300, options.length * 40); // Approximate height

        // Start with position at cursor
        let posX = Math.max(16, rect.left);
        let posY = rect.bottom;

        // Adjust if it would go off-screen
        if (posX + dropdownWidth > viewportWidth - 16) {
          posX = Math.max(16, viewportWidth - dropdownWidth - 16);
        }

        if (posY + dropdownHeight > viewportHeight - 16) {
          // Position above if not enough space below
          posY = Math.max(16, rect.top - dropdownHeight - 4);
        }

        // Only store the initial position if we haven't positioned yet
        // This prevents the menu from jumping around during interactions
        if (!hasPositionedRef.current && posX > 0 && posY > 0) {
          stablePositionRef.current = { left: posX, top: posY };
          hasPositionedRef.current = true;
        }

        // Fallback: if we somehow got zero values earlier, fix them now
        if (
          !stablePositionRef.current ||
          stablePositionRef.current.left <= 0 ||
          stablePositionRef.current.top <= 0
        ) {
          stablePositionRef.current = { left: posX, top: posY };
        }

        return ReactDOM.createPortal(
          <div
            id="typeahead-menu"
            className={classNames(
              "typeahead-popover mentions-menu",
              DropdownClassName,
              "sm:min-w-[300px] lg:min-w-[400px] p-1 bg-zinc-950 text-zinc-300 text-xs overflow-x-hidden overflow-y-auto z-50",
            )}
            style={{
              position: "fixed",
              left: `${posX}px`,
              top: `${posY}px`,
              // left: stablePositionRef.current?.left
              //   ? `${stablePositionRef.current.left}px`
              //   : "16px",
              // top: stablePositionRef.current?.top ? `${stablePositionRef.current.top}px` : "16px",
              maxWidth: "400px",
              maxHeight: "300px",
              // Use !important to prevent any style overrides from the Lexical menu
              transform: "none !important",
              transition: "none !important", // Prevent animations that could cause shifting
            }}
          >
            <ul className="max-h-[250px] overflow-y-auto">
              {options.map((option, i: number) => (
                <MentionsTypeaheadMenuItem
                  index={i}
                  isSelected={selectedIndex === i}
                  onClick={() => {
                    setHighlightedIndex(i);
                    selectOptionAndCleanUp(option);
                  }}
                  onMouseEnter={() => {
                    setHighlightedIndex(i);
                  }}
                  key={option.key}
                  option={option}
                />
              ))}
            </ul>
          </div>,
          anchorElementRef.current,
        );
      }}
    />
  );
}

export function getMentionNodesFromSerializedPromptEditorState(
  node: SerializedLexicalNode,
): SerializedMentionNode[] {
  let mentions: SerializedMentionNode[] = [];

  // Check if the current node is a mention node
  if (isSerializedMentionNode(node)) {
    mentions.push({
      detail: node.detail,
      format: node.format,
      mode: node.mode,
      style: node.style,
      text: node.text,
      type: node.type,
      version: node.version,
      modelId: node.modelId,
      modelDisplayName: node.modelDisplayName,
      trainingId: node.trainingId,
      trainingDisplayName: node.trainingDisplayName,
      caption: node.caption,
      scale: node.scale,
    });
  }

  // If the node has children, recurse through each child

  const children = (node as any as SerializedRootNode).children;

  if (children && children.length > 0) {
    for (const child of children) {
      mentions = mentions.concat(getMentionNodesFromSerializedPromptEditorState(child));
    }
  }

  return mentions;
}

function getTextFromSerializedMentionNode(node: SerializedMentionNode, replaceWithText = false) {
  const { modelId, text } = node;

  if (replaceWithText) {
    return text;
  }

  return getModelTriggerWord({
    modelId,
  });
}

function replaceMentionNodesWithTextNodes<T extends SerializedLexicalNode>(
  node: T,
  replaceWithText = false,
): T {
  if (isSerializedMentionNode(node)) {
    const textNode: SerializedTextNode = {
      type: "text",
      detail: node.detail,
      text: getTextFromSerializedMentionNode(node, replaceWithText),
      format: node.format,
      mode: node.mode,
      style: node.style,
      version: node.version,
    };
    return textNode as any as T;
  }

  const children = (node as any as SerializedRootNode).children;

  if (children && children.length > 0) {
    const newChildren = children.map((child) =>
      replaceMentionNodesWithTextNodes(child, replaceWithText),
    );
    return {
      ...node,
      children: newChildren,
    };
  }

  return node;
}

export function forEachPromptEditorMentionNode<T extends SerializedLexicalNode>(
  node: T,
  callback: (node: SerializedMentionNode) => void,
): void {
  if (isSerializedMentionNode(node)) {
    callback(node);
    return;
  }

  const children = (node as unknown as SerializedRootNode).children;
  if (children && children.length > 0) {
    children.forEach((child) => forEachPromptEditorMentionNode(child, callback));
  }
}

export async function processAllPromptEditorMentionNodes<T extends SerializedLexicalNode>(
  node: T,
  process: (node: SerializedMentionNode) => Promise<SerializedLexicalNode>,
): Promise<T> {
  if (isSerializedMentionNode(node)) {
    const processedNode = await process(node);

    return processedNode as any as T;
  }

  const children = (node as any as SerializedRootNode).children;

  if (children && children.length > 0) {
    const newChildren = await Promise.all(
      children.map((child) => processAllPromptEditorMentionNodes(child, process)),
    );
    return {
      ...node,
      children: newChildren,
    };
  }

  return node;
}

type ParseCustomModelPromptEditorStateOutput = {
  prompt: string;
  modelConfigs: CustomModelScaleConfigs;
};

function getCustomModelScaleConfigFromSerializedMentionNode(
  mentionNode: SerializedMentionNode,
): CustomModelScaleConfig {
  return {
    scale: mentionNode.scale,
    modelId: mentionNode.modelId,
    trainingId: mentionNode.trainingId,
  };
}

function parseCustomModelPromptEditorState({
  promptEditorState,
  replaceWithText = false,
}: {
  promptEditorState: SerializedEditorState;
  replaceWithText?: boolean;
}): ParseCustomModelPromptEditorStateOutput {
  // Find all mention nodes in the JSON

  const mentionNodes = getMentionNodesFromSerializedPromptEditorState(promptEditorState.root);

  const modelConfigs = Object.fromEntries<CustomModelScaleConfig>(
    mentionNodes.map((mentionNode) => [
      mentionNode.modelId,
      getCustomModelScaleConfigFromSerializedMentionNode(mentionNode),
    ]),
  );

  const cleanedPromptEditorState = {
    ...promptEditorState,
    root: replaceMentionNodesWithTextNodes(promptEditorState.root, replaceWithText),
  };

  const prompt =
    getTextContentFromPromptState(JSON.stringify(cleanedPromptEditorState), {
      nodes: [MentionNode, MentionCaptionNode],
    }) ?? "";

  return {
    prompt,
    modelConfigs,
  };
}

export function getCustomModelPlaygroundPromptEditorStateFromSerializedEditorState({
  promptEditorState,
  replaceWithText = false,
}: {
  promptEditorState: SerializedEditorState;
  replaceWithText?: boolean;
}): CustomModelPlaygroundPromptEditorState {
  const { prompt, modelConfigs } = parseCustomModelPromptEditorState({
    promptEditorState,
    replaceWithText,
  });

  const json = JSON.stringify(promptEditorState);

  // Get the prompt editor state

  return {
    json,
    text: prompt,
    scaleConfigs: modelConfigs,
    isUpdatingPrompt: false,
    isUpdatingPromptType: null,
  };
}

export function getSerializedPromptEditorStateFromPromptJson(
  promptJson: string,
): SerializedEditorState {
  try {
    return JSON.parse(promptJson);
  } catch (error) {
    debugError("[getSerializedPromptEditorStateFromPromptJson] Error parsing prompt json: ", error);
    return {
      root: {
        children: [],
        direction: "ltr",
        format: "",
        indent: 0,
        type: "root",
        version: 1,
      },
    };
  }
}

export async function correctCustomModelPromptEditorStateDisplayNames({
  backend,
  promptEditorState: inputPromptEditorState,
}: {
  promptEditorState: SerializedEditorState | string;
  backend: Backend;
}): Promise<SerializedEditorState> {
  const promptEditorState: SerializedEditorState =
    typeof inputPromptEditorState === "string"
      ? getSerializedPromptEditorStateFromPromptJson(inputPromptEditorState)
      : inputPromptEditorState;

  try {
    return {
      ...promptEditorState,
      root: await processAllPromptEditorMentionNodes(promptEditorState.root, async (node) => {
        const { modelId, modelDisplayName, trainingId, trainingDisplayName } = node;

        const [modelInfo, trainingInfo] = await Promise.all([
          backend.getCustomModelInfo(modelId),
          backend.getCustomModelTraining({
            modelId,
            trainingId,
          }),
        ]);

        const modelDisplayNameModified = modelInfo?.displayName || modelDisplayName;
        const trainingDisplayNameModified = trainingInfo?.displayName || trainingDisplayName;
        const text = getModelTrainingMentionName({
          modelDisplayName: modelDisplayNameModified,
          training: {
            displayName: trainingDisplayNameModified,
            id: trainingId,
          },
        });

        return {
          ...node,
          text,
          modelDisplayName: modelDisplayNameModified,
          trainingDisplayName: trainingDisplayNameModified,
        };
      }),
    };
  } catch (error) {
    debugError("Cannot correct custom model prompt editor state:\n", error);
    return promptEditorState;
  }
}
